import React, { useState, useMemo } from 'react';

// bootstrap
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';

// tremor
import { Subtitle } from '@tremor/react';

// styles
import { Spacing, Colors } from '_styles';

// Duration options
const durationOptions = [
    { label: '15 min', value: '15 min' },
    { label: '30 min', value: '30 min' },
    { label: '45 min', value: '45 min' },
    { label: '50 min', value: '50 min' },
    { label: '55 min', value: '55 min' },
    { label: '1 hr', value: '1 hr' },
    { label: '1 hr 10 min', value: '1 hr 10 min' },
    { label: '1 hr 15 min', value: '1 hr 15 min' },
    { label: '1 hr 30 min', value: '1 hr 30 min' },
    { label: '1 hr 45 min', value: '1 hr 45 min' },
    { label: '2 hrs', value: '2 hrs' },
];

// Helper to format time as HH:mm
const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

// Helper to format time as HH:mm am/pm
const formatTimeWithAmPm = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
};

// Round to nearest 5-minute interval
const roundToNearest5 = (date) => {
    const minutes = date.getMinutes();
    const roundedMinutes = Math.round(minutes / 5) * 5;
    
    const result = new Date(date);
    result.setMinutes(roundedMinutes, 0, 0);
    
    return result;
};

// Get the next 5-minute interval from a given date (rounds UP)
const getNext5Min = (date) => {
    const minutes = date.getMinutes();
    const remainder = minutes % 5;
    // Always round UP to the next 5-minute mark
    const roundedMinutes = remainder === 0 ? minutes : minutes + (5 - remainder);
    
    const result = new Date(date);
    result.setMinutes(roundedMinutes, 0, 0);
    
    return result;
};

// Get three consecutive 5-minute intervals starting from next 5-min mark
const getQuickTimeOptions = (date) => {
    const first = getNext5Min(date);
    
    const second = new Date(first);
    second.setMinutes(second.getMinutes() + 5);
    
    const third = new Date(second);
    third.setMinutes(third.getMinutes() + 5);
    
    return [formatTime(first), formatTime(second), formatTime(third)];
};

// Generate 5-minute interval options for dropdown
const generate5MinIntervals = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 5) {
            const hourStr = String(hour).padStart(2, '0');
            const minuteStr = String(minute).padStart(2, '0');
            options.push(`${hourStr}:${minuteStr}`);
        }
    }
    return options;
};

const timeIntervalOptions = generate5MinIntervals();

// Parse duration string to minutes
const parseDurationToMinutes = (duration) => {
    if (!duration) return 0;
    let minutes = 0;
    if (duration.includes('hr')) {
        const hrs = parseInt(duration, 10) || 0;
        minutes += hrs * 60;
    }
    const minMatch = duration.match(/(\d+)\s*min/);
    if (minMatch) {
        minutes += parseInt(minMatch[1], 10);
    }
    // Handle "1 hr", "2 hrs" without min
    if (!duration.includes('min') && duration.includes('hr')) {
        return minutes;
    }
    return minutes;
};

// Calculate end time from start + duration
const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return null;
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const durationMinutes = parseDurationToMinutes(duration);
    
    const totalMinutes = hours * 60 + minutes + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMinutes = totalMinutes % 60;
    
    return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`;
};

const BreakInputForm = (props) => {
    const [breakStartTime, setBreakStartTime] = useState('');
    const [breakDuration, setBreakDuration] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());

    // Update current time every 30 seconds to keep quick buttons accurate
    React.useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 30000);
        return () => clearInterval(interval);
    }, []);

    const [quickTime1, quickTime2, quickTime3] = getQuickTimeOptions(currentTime);
    const nowRounded = formatTime(roundToNearest5(currentTime));

    const handleSetNow = () => setBreakStartTime(formatTime(roundToNearest5(new Date())));
    const handleQuickTime1 = () => setBreakStartTime(getQuickTimeOptions(new Date())[0]);
    const handleQuickTime2 = () => setBreakStartTime(getQuickTimeOptions(new Date())[1]);
    const handleQuickTime3 = () => setBreakStartTime(getQuickTimeOptions(new Date())[2]);

    const breakEndTime = useMemo(() => 
        calculateEndTime(breakStartTime, breakDuration), 
        [breakStartTime, breakDuration]
    );

    // Calculate minutes until break starts (negative if in the past)
    const minutesUntilBreak = useMemo(() => {
        if (!breakStartTime) return null;
        const [hours, minutes] = breakStartTime.split(':').map(Number);
        const now = new Date();
        const breakStart = new Date(now);
        breakStart.setHours(hours, minutes, 0, 0);
        const diffMs = breakStart - now;
        return Math.round(diffMs / 60000); // Convert to minutes
    }, [breakStartTime, currentTime]);

    const isFormComplete = breakStartTime && breakDuration && breakEndTime;

    const handleCancel = () => {
        setBreakStartTime('');
        setBreakDuration('');
        props?.onCancel?.();
    };

    const handleConfirm = () => {
        if (!isFormComplete) return;
        props?.onConfirm?.({
            breakStartTime,
            breakEndTime,
            breakDuration
        });
    };

    return (
        <div style={{ marginTop: Spacing.SCALE_22 }}>

            {/* Quick Set Buttons */}
            <Row style={{ marginBottom: Spacing.SCALE_22 }}>
                <Col xs={12} md={6}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Quick Set Start Time</label>
                    <p style={{ fontSize: '13px', color: '#888', margin: '0 0 10px 0' }}>
                        Select the next available 5-minute interval
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Button variant="outline-secondary" size="sm" onClick={handleSetNow}>
                            Now ({nowRounded})
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={handleQuickTime1}>
                            {quickTime1}
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={handleQuickTime2}>
                            {quickTime2}
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={handleQuickTime3}>
                            {quickTime3}
                        </Button>
                    </div>
                </Col>
                <Col xs={12} md={6}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Quick Set Duration</label>
                    <p style={{ fontSize: '13px', color: '#888', margin: '0 0 10px 0' }}>
                        Select a common break duration
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Button variant="outline-secondary" size="sm" onClick={() => setBreakDuration('15 min')}>
                            15 min
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => setBreakDuration('50 min')}>
                            50 min
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => setBreakDuration('1 hr 10 min')}>
                            70 min
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Inputs */}
            <Row style={{ marginBottom: Spacing.SCALE_22 }}>
                <Col xs={12} md={6}>
                    <Subtitle style={{ fontSize: '14px' }}>Break Start Time (5-min intervals)</Subtitle>
                    <Form.Select
                        value={breakStartTime}
                        onChange={(e) => setBreakStartTime(e.target.value)}
                        style={{ fontSize: '16px' }}
                    >
                        <option value="">Select start time</option>
                        {timeIntervalOptions.map((time) => (
                            <option key={time} value={time}>{time}</option>
                        ))}
                    </Form.Select>
                </Col>
                <Col xs={12} md={6}>
                    <Subtitle style={{ fontSize: '14px' }}>Break Duration</Subtitle>
                    <Form.Select
                        value={breakDuration}
                        onChange={(e) => setBreakDuration(e.target.value)}
                        style={{ fontSize: '16px' }}
                    >
                        <option value="">Select duration</option>
                        {durationOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            {/* Break Summary */}
            <div style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: Spacing.SCALE_12, 
                    borderRadius: '6px',
                    marginBottom: Spacing.SCALE_12,
                    border: '1px solid #e9ecef'
                }}>
                    <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '10px', display: 'block' }}>Break Summary</label>
                    <Row>
                        <Col xs={6} md={4}>
                            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 4px 0' }}>Start Time</p>
                            <p style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{formatTimeWithAmPm(breakStartTime) || '--:--'}</p>
                        </Col>
                        <Col xs={6} md={4}>
                            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 4px 0' }}>End Time</p>
                            <p style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{formatTimeWithAmPm(breakEndTime) || '--:--'}</p>
                        </Col>
                        <Col xs={12} md={4}>
                            <p style={{ fontSize: '13px', color: '#666', margin: '0 0 4px 0' }}>Duration</p>
                            <p style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>{breakDuration || '--'}</p>
                        </Col>
                    </Row>
            </div>

            {/* Time Status Message */}
            {breakStartTime && breakDuration && minutesUntilBreak !== null && (
                <div style={{ 
                    backgroundColor: minutesUntilBreak < 0 ? '#fff3cd' : '#d4edda', 
                    padding: Spacing.SCALE_12, 
                    borderRadius: '6px',
                    marginBottom: Spacing.SCALE_22,
                    border: `1px solid ${minutesUntilBreak < 0 ? '#ffc107' : '#c3e6cb'}`
                }}>
                    <p style={{ 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        margin: 0,
                        color: minutesUntilBreak < 0 ? '#856404' : '#155724'
                    }}>
                        {minutesUntilBreak < 0 
                            ? `⚠️ Break start is ${Math.abs(minutesUntilBreak)} minute${Math.abs(minutesUntilBreak) !== 1 ? 's' : ''} in the past`
                            : minutesUntilBreak === 0
                                ? `Break starts now`
                                : `Break starts in ${minutesUntilBreak} minute${minutesUntilBreak !== 1 ? 's' : ''} time`
                        }
                    </p>
                </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <Button 
                    variant="outline-secondary" 
                    onClick={handleCancel}
                    disabled={props?.isSubmitting || props?.disabled}
                >
                    Reset
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirm}
                    disabled={!isFormComplete || props?.isSubmitting || props?.disabled}
                    style={{ backgroundColor: Colors.PRM_LIGHT_BLUE, borderColor: Colors.PRM_LIGHT_BLUE }}
                >
                    {props?.isSubmitting ? 'Assigning...' : 'Confirm Break'}
                </Button>
            </div>
        </div>
    );
};

export default BreakInputForm;
